// src/Dashboard/admin/components/InstructorChurchesModal.jsx
import { useMemo } from "react";
import { ModalShell } from "./ui";

export default function InstructorChurchesModal({
  open,
  onClose,

  instructor, // row object
  loading = false,
  error,

  churches = [], // already scoped list from AdminDashboard
  selectedIds = [],
  setSelectedIds,

  search = "",
  setSearch,

  onSave,
  onClear,

  // helpers from AdminDashboard
  getIsActive,
  getChurchId,
}) {
  // ✅ Hooks must be called unconditionally (before any early return)
  const subtitle =
    instructor?.full_name || instructor?.fullName || instructor?.email || "Instructor";

  const q = useMemo(() => String(search || "").trim().toLowerCase(), [search]);

  const safeSelected = useMemo(() => {
    return Array.isArray(selectedIds) ? selectedIds.map(String) : [];
  }, [selectedIds]);

  const filteredChurches = useMemo(() => {
    const list = Array.isArray(churches) ? churches : [];

    return list
      .filter((c) => (typeof getIsActive === "function" ? getIsActive(c) !== "0" : true))
      .filter((c) => {
        if (!q) return true;

        const id = String(typeof getChurchId === "function" ? getChurchId(c) : c?.id || "")
          .toLowerCase()
          .trim();

        const name = String(c?.name || "").toLowerCase();

        return name.includes(q) || id.includes(q);
      });
  }, [churches, getIsActive, getChurchId, q]);

  // ✅ Now it's safe to early-return
  if (!open) return null;

  const toggleId = (id, checked) => {
    const cid = String(id || "").trim();
    if (!cid) return;

    if (checked) {
      setSelectedIds?.(Array.from(new Set([...safeSelected, cid])));
    } else {
      setSelectedIds?.(safeSelected.filter((x) => String(x) !== cid));
    }
  };

  return (
    <ModalShell
      title="Manage Instructor Churches"
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onSave}
            disabled={!!loading}
            className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold disabled:opacity-60"
          >
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      }
    >
      {error ? (
        <div className="mb-3 p-3 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-100">
          {String(error)}
        </div>
      ) : null}

      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3 min-w-0">
        <input
          value={search}
          onChange={(e) => setSearch?.(e.target.value)}
          placeholder="Search church…"
          className="w-full sm:flex-1 px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm"
        />

        <button
          type="button"
          onClick={onClear}
          className="w-full sm:w-auto px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
          title="Clear selection"
        >
          Clear
        </button>
      </div>

      <div className="max-h-[420px] overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 dark:border-slate-800">
        {filteredChurches.map((c) => {
          const id = String(
            typeof getChurchId === "function" ? getChurchId(c) : c?.id || ""
          ).trim();
          if (!id) return null;

          const checked = safeSelected.includes(id);

          return (
            <label
              key={id}
              className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer min-w-0"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => toggleId(id, e.target.checked)}
                className="w-4 h-4 shrink-0"
              />
              <div className="min-w-0">
                <div className="font-semibold truncate">{c?.name || "—"}</div>
                <div className="text-xs text-slate-500 font-mono truncate">{id}</div>
              </div>
            </label>
          );
        })}

        {filteredChurches.length === 0 ? (
          <div className="px-4 py-10 text-sm text-slate-500">
            {q ? "No churches match your search." : "No churches available in this scope."}
          </div>
        ) : null}
      </div>

      <div className="mt-3 text-xs text-slate-500 flex items-center justify-between gap-3">
        <div className="truncate">
          Selected: <span className="font-semibold">{safeSelected.length}</span>
        </div>
        {q ? <div className="text-slate-400 shrink-0">{filteredChurches.length} shown</div> : null}
      </div>
    </ModalShell>
  );
}
